// apps/vendor/src/lib/vendor-failure-copy.ts
//
// ENG-2952 — vendor's bound entry point into the shared three-step ladder in
// @clearpath/permissions-ui, ruled by
// docs/architecture-decisions/ENG-2539-operator-error-copy.md. vendor is a
// federated-inbox surface for Mode A vendor-tenant staff (see
// apps/vendor/README.md) — an external-partner audience, never an internal
// lab role — so this is a near-verbatim sibling of
// apps/portal/src/lib/portal-failure-copy.ts, not a new design.
//
// This module holds COPY and ONE bound helper, nothing else. The ladder
// itself (deny rung, domain composer, generic) lives entirely in
// `@clearpath/permissions-ui`'s `failureCopy` — the ruled home for it — so
// vendor does not grow its own copy of Cedar/RBAC classification.
// `vendorFailureCopy` below is `failureCopy` with two arguments
// pre-applied:
//
//   - `audience: 'customer'` — narrows the deny rung the way the ADR
//     describes (never an internal publishing-workflow sentence, never a
//     label derived from a permission key the generated registry hasn't
//     caught up to).
//   - `onError: console.error` — logs the ORIGINAL error object once per
//     call, before any copy is returned. Client-console-only, no egress.
//
// `VendorFailureCopyOptions` omits `correlationId` — vendor carries no
// reference id on a rendered sentence, matching the portal ruling.
// `vendorFailureCopy` also deletes it from the options object before
// forwarding: the type alone rejects only an inline literal, while an
// options object built in a variable or spread from a wider one still
// carried an id through to the rendered sentence.

import { failureCopy, type FailureCopyOptions } from '@clearpath/permissions-ui';

const RETRY_CLAUSE = 'Try again, or contact support if it keeps happening.';

/** A load-failure sentence naming the subject, e.g. `vendorLoadFailureSentence('federated work')`
 *  -> "We could not load your federated work. Try again, or contact support if it keeps happening." */
export function vendorLoadFailureSentence(subject: string): string {
  return `We could not load your ${subject}. ${RETRY_CLAUSE}`;
}

/** An action-failure sentence naming the action, e.g. `vendorActionFailureSentence('complete this step')`
 *  -> "We could not complete this step. Try again, or contact support if it keeps happening." */
export function vendorActionFailureSentence(action: string): string {
  return `We could not ${action}. ${RETRY_CLAUSE}`;
}

/** Subject-less retry sentence for a surface whose own heading already names
 *  the subject — use this instead of `vendorLoadFailureSentence` there. */
export const VENDOR_RETRY_SENTENCE: string = RETRY_CLAUSE;

export type VendorFailureCopyOptions = Omit<FailureCopyOptions, 'audience' | 'onError' | 'correlationId'>;

/**
 * vendor's one bound entry point into the shared ladder — `audience:
 * 'customer'` and the console-logging `onError` are fixed here so no call
 * site can omit either. Pass `generic` (one of the sentences above, or
 * `null` when the surface should render no description node on an
 * unrecognized failure) and, optionally, `compose` for a recognized
 * domain shape.
 */
export function vendorFailureCopy(err: unknown, options: VendorFailureCopyOptions): string | null {
  // Discarded explicitly rather than relied on being absent — see the module
  // docblock's `Omit` note.
  const { correlationId: _discarded, ...rest } = options as VendorFailureCopyOptions & {
    correlationId?: unknown;
  };
  void _discarded;
  return failureCopy(err, {
    ...rest,
    audience: 'customer',
    correlationId: null,
    onError: (original) => {
      console.error('[vendor] failure', original);
    },
  });
}
